import { type KeyboardEvent, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { useLocation } from 'react-router'
import { Alert, Button, DividerCustom, Error, Typography } from 'shared/ui'
import { InputPassword } from 'shared/ui/TextField/InputPresets/InputPassword'
import { makeTextFieldAutoFocus, makeTextFieldHighlight, TextField } from 'shared/ui/TextField'
import { Label } from 'shared/ui/Label'
import { uiStore } from 'shared/store/uiStore'
import { getAriaLabel } from 'shared/lib'
import { TermsAndPolicy } from 'entities/Auth'
import { useEntitiesContext } from 'entities/EntitiesExport'

import { type LoginStore } from 'pages/auth/pages/login/ui/Login'
import { AuthPath } from 'pages/auth/route/authPath'
import { RegisterPath } from 'pages/register/route/registerPath'
import styles from './styles.module.scss'

type IFormProps = {
  store: LoginStore
}

export const LoginForm = observer(({ store }: IFormProps) => {
  const { state } = useLocation()
  const { authStore } = useEntitiesContext()

  const {
    setEmail,
    email,
    password,
    setPassword,
    loading,
    emailError,
    passwordError,
    error,
    triggerEmailValidation,
    triggerPasswordValidation,
    signIn,
    hasAlertErrors,
    alertErrors,
    isValidEmail,
    hasPassword,
    setAlertSuccess,
    alertSuccess,
    alertWarning,
    showConstantEmailError,
    showConstantPasswordError,
    showConstantErrors,
  } = store

  const { googleAuthStore } = authStore

  const handleSignIn = async () => {
    showConstantErrors()
    signIn()
  }

  const onKeyDownEmail = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      triggerEmailValidation()
      if (hasPassword && isValidEmail) signIn()
    }
  }

  const onKeyDownPassword = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      triggerPasswordValidation()
      if (hasPassword && isValidEmail) signIn()
    }
  }

  const handleResetPassword = () => {
    uiStore.changeRoute({
      path: `/${AuthPath.forgot}`,
      options: {
        state: { email },
      },
    })
  }

  const onSignUp = () => {
    uiStore.changeRoute({
      path: `/${RegisterPath.root}`,
    })
  }

  useEffect(() => {
    if (state?.alert) setAlertSuccess(state?.alert)
  }, [])

  return (
    <div className={styles.wrap}>
      {error && (
        <Alert
          className={styles.error}
          item={{
            type: 'error',
            desc: error,
          }}
        />
      )}

      <Button
        className={classNames(styles.button, styles.signIn)}
        loading={googleAuthStore.loading}
        disabled={googleAuthStore.loading || loading}
        typeBtn='outlined'
        type='button'
        icon={'google'}
        text={'Sign in with Google'}
        contained={'secondary'}
        onClick={googleAuthStore.onAuth}
        fullWidth
      />

      <DividerCustom variant='text' className={styles.divider}>
        <Typography
          variant={'body-rg-regular'}
          ariaLabel={getAriaLabel('orDivider')}
          color={'var(--content-primary-tertiary)'}
        >
          OR
        </Typography>
      </DividerCustom>

      {(hasAlertErrors || alertSuccess || alertWarning) && (
        <div className={styles.alertWrap}>
          {alertErrors.map((error) => (
            <Alert
              key={error}
              item={{
                type: 'alert',
                desc: error,
              }}
            />
          ))}
          {alertSuccess && (
            <Alert
              item={{
                type: 'success',
                desc: alertSuccess,
              }}
              onClose={() => setAlertSuccess('')}
            />
          )}
          {alertWarning && (
            <Alert
              item={{
                type: 'warning',
                desc: alertWarning,
              }}
            />
          )}
        </div>
      )}

      <Label
        label={{
          text: 'Email address',
          className: styles.inputLabel,
        }}
      />
      <TextField
        className={classNames(styles.textField, { [styles.error]: !!emailError })}
        variant='stroke'
        disabled={googleAuthStore.loading || loading}
        size='medium'
        InputProps={{ placeholder: 'name@company.com', onKeyDown: onKeyDownEmail }}
        onChange={setEmail}
        error={!!emailError && showConstantEmailError}
        value={email}
        mainActions={[
          makeTextFieldAutoFocus({ withFocus: true }),
          makeTextFieldHighlight({ mode: 'error', highlight: !!emailError }),
        ]}
      />
      <Error type={'text'} error={emailError} />

      <Label
        label={{
          text: 'Password',
          className: styles.inputLabel,
          rightText: (
            <Button
              paddingInline={0}
              onClick={handleResetPassword}
              typeBtn={'link'}
              text={'Forgot your password?'}
              tag='span'
            />
          ),
        }}
      />
      <TextField
        Input={InputPassword}
        className={classNames(styles.textField, {
          [styles.error]: !!passwordError,
        })}
        variant='stroke'
        InputProps={{
          placeholder: 'Enter your password',
          onKeyDown: onKeyDownPassword,
        }}
        error={!!passwordError && showConstantPasswordError}
        disabled={googleAuthStore.loading || loading}
        size='medium'
        onChange={setPassword}
        value={password}
        mainActions={[makeTextFieldHighlight({ mode: 'error', highlight: !!passwordError })]}
      />
      <Error type={'text'} error={passwordError} />

      <div className={styles.footer}>
        <Button
          className={classNames(styles.button, styles.signIn)}
          typeBtn='contained'
          type='button'
          onClick={handleSignIn}
          loading={loading}
          disabled={googleAuthStore.loading || loading}
          text={'Sign in'}
          contained={'primary'}
          fullWidth
        />
        <TermsAndPolicy />
        <div className={styles.bottom}>
          <Typography
            variant={'body-sm-regular'}
            ariaLabel={'signUpLink'}
            color={'var(--content-primary-tertiary)'}
          >
            {'Don’t have a Salesmsg account?'}&nbsp;
            <Typography
              link
              className={styles.link}
              variant={'body-sm-medium'}
              ariaLabel={'signUp'}
              onClick={onSignUp}
            >
              Sign up
            </Typography>
          </Typography>
        </div>
      </div>
    </div>
  )
})

export default LoginForm
